import { useRef, useEffect, useMemo, useState } from "react";

const PatternShapes = { Checks: 0, Stripes: 1, Edge: 2 };

const presets = {
  Lava: {
    color1: "#FF9F21", color2: "#FF0303", color3: "#000000",
    rotation: 114, proportion: 100, scale: 0.52, speed: 30,
    distortion: 7, swirl: 18, swirlIterations: 20, softness: 100,
    offset: 717, shape: "Edge", shapeSize: 12,
  },
  Ember: {
    color1: "#4E6C7E", color2: "#3A5468", color3: "#0B1220",
    rotation: 114, proportion: 80, scale: 0.45, speed: 22,
    distortion: 5, swirl: 25, swirlIterations: 14, softness: 90,
    offset: 400, shape: "Edge", shapeSize: 20,
  },
  Plasma: {
    color1: "#B566FF", color2: "#000000", color3: "#000000",
    rotation: 0, proportion: 63, scale: 0.75, speed: 30,
    distortion: 5, swirl: 61, swirlIterations: 5, softness: 100,
    offset: -168, shape: "Checks", shapeSize: 28,
  },
  Prism: {
    color1: "#050505", color2: "#66B3FF", color3: "#FFFFFF",
    rotation: -50, proportion: 1, scale: 0.01, speed: 30,
    distortion: 0, swirl: 50, swirlIterations: 16, softness: 47,
    offset: -299, shape: "Checks", shapeSize: 45,
  },
};

const FRAGMENT_SHADER = `#version 300 es
precision highp float;
uniform float u_time;
uniform float u_pixelRatio;
uniform vec2 u_resolution;
uniform float u_scale;
uniform float u_rotation;
uniform vec4 u_color1;
uniform vec4 u_color2;
uniform vec4 u_color3;
uniform float u_proportion;
uniform float u_softness;
uniform float u_shape;
uniform float u_shapeScale;
uniform float u_distortion;
uniform float u_swirl;
uniform float u_swirlIterations;
out vec4 fragColor;
#define TWO_PI 6.28318530718
#define PI 3.14159265358979323846
vec2 rotate(vec2 uv, float th) {
  return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
}
float random(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}
float noise(vec2 st) {
  vec2 i = floor(st); vec2 f = fract(st);
  float a = random(i); float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0)); float d = random(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
vec4 blend_colors(vec4 c1, vec4 c2, vec4 c3, float mixer, float edgesWidth, float edge_blur) {
    vec3 color1 = c1.rgb * c1.a; vec3 color2 = c2.rgb * c2.a; vec3 color3 = c3.rgb * c3.a;
    float r1 = smoothstep(.0 + .35 * edgesWidth, .7 - .35 * edgesWidth + .5 * edge_blur, mixer);
    float r2 = smoothstep(.3 + .35 * edgesWidth, 1. - .35 * edgesWidth + edge_blur, mixer);
    vec3 blended_color_2 = mix(color1, color2, r1);
    float blended_opacity_2 = mix(c1.a, c2.a, r1);
    vec3 c = mix(blended_color_2, color3, r2);
    float o = mix(blended_opacity_2, c3.a, r2);
    return vec4(c, o);
}
void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    float t = .5 * u_time;
    float noise_scale = .0005 + .006 * u_scale;
    uv -= .5;
    uv *= (noise_scale * u_resolution);
    uv = rotate(uv, u_rotation * .5 * PI);
    uv /= u_pixelRatio;
    uv += .5;
    float n1 = noise(uv * 1. + t);
    float n2 = noise(uv * 2. - t);
    float angle = n1 * TWO_PI;
    uv.x += 4. * u_distortion * n2 * cos(angle);
    uv.y += 4. * u_distortion * n2 * sin(angle);
    float iterations_number = ceil(clamp(u_swirlIterations, 1., 30.));
    for (float i = 1.; i <= iterations_number; i++) {
        uv.x += clamp(u_swirl, 0., 2.) / i * cos(t + i * 1.5 * uv.y);
        uv.y += clamp(u_swirl, 0., 2.) / i * cos(t + i * 1. * uv.x);
    }
    float proportion = clamp(u_proportion, 0., 1.);
    float shape = 0.; float mixer = 0.;
    if (u_shape < .5) {
      vec2 checks_shape_uv = uv * (.5 + 3.5 * u_shapeScale);
      shape = .5 + .5 * sin(checks_shape_uv.x) * cos(checks_shape_uv.y);
      mixer = shape + .48 * sign(proportion - .5) * pow(abs(proportion - .5), .5);
    } else if (u_shape < 1.5) {
      vec2 stripes_shape_uv = uv * (.25 + 3. * u_shapeScale);
      float f = fract(stripes_shape_uv.y);
      shape = smoothstep(.0, .55, f) * smoothstep(1., .45, f);
      mixer = shape + .48 * sign(proportion - .5) * pow(abs(proportion - .5), .5);
    } else {
      float sh = 1. - uv.y; sh -= .5; sh /= (noise_scale * u_resolution.y); sh += .5;
      float shape_scaling = .2 * (1. - u_shapeScale);
      shape = smoothstep(.45 - shape_scaling, .55 + shape_scaling, sh + .3 * (proportion - .5));
      mixer = shape;
    }
    vec4 color_mix = blend_colors(u_color1, u_color2, u_color3, mixer, 1. - clamp(u_softness, 0., 1.), .01 + .01 * u_scale);
    fragColor = vec4(color_mix.rgb, color_mix.a);
}`;

function hexToRgba(hex) {
  let r = 0, g = 0, b = 0, a = 1;
  if (hex.startsWith("#")) {
    const c = hex.slice(1);
    if (c.length === 3) {
      r = parseInt(c[0]+c[0],16)/255; g = parseInt(c[1]+c[1],16)/255; b = parseInt(c[2]+c[2],16)/255;
    } else {
      r = parseInt(c.slice(0,2),16)/255; g = parseInt(c.slice(2,4),16)/255; b = parseInt(c.slice(4,6),16)/255;
      if (c.length === 8) a = parseInt(c.slice(6,8),16)/255;
    }
  }
  return [r, g, b, a];
}

export default function AnimatedGradient({ preset = "Ember", customParams, style, className }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const frameIdRef = useRef(undefined);
  const startTimeRef = useRef(0);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => { setIsMounted(true); return () => setIsMounted(false); }, []);

  const params = useMemo(() => {
    if (customParams) return customParams;
    return presets[preset] || presets.Ember;
  }, [preset, customParams]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !isMounted) return;

    const gl = canvas.getContext("webgl2", { premultipliedAlpha: true, alpha: true, antialias: true });
    if (!gl) return;

    const vertSrc = `#version 300 es\nin vec4 a_position;\nvoid main() { gl_Position = a_position; }`;
    const vert = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vert, vertSrc); gl.compileShader(vert);
    const frag = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(frag, FRAGMENT_SHADER); gl.compileShader(frag);
    const program = gl.createProgram();
    gl.attachShader(program, vert); gl.attachShader(program, frag);
    gl.linkProgram(program); gl.useProgram(program);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const u = {
      time: gl.getUniformLocation(program, "u_time"),
      res: gl.getUniformLocation(program, "u_resolution"),
      px: gl.getUniformLocation(program, "u_pixelRatio"),
      scale: gl.getUniformLocation(program, "u_scale"),
      rot: gl.getUniformLocation(program, "u_rotation"),
      c1: gl.getUniformLocation(program, "u_color1"),
      c2: gl.getUniformLocation(program, "u_color2"),
      c3: gl.getUniformLocation(program, "u_color3"),
      prop: gl.getUniformLocation(program, "u_proportion"),
      soft: gl.getUniformLocation(program, "u_softness"),
      shape: gl.getUniformLocation(program, "u_shape"),
      shapeScale: gl.getUniformLocation(program, "u_shapeScale"),
      dist: gl.getUniformLocation(program, "u_distortion"),
      swirl: gl.getUniformLocation(program, "u_swirl"),
      swirlIt: gl.getUniformLocation(program, "u_swirlIterations"),
    };

    const resize = () => {
      const w = container.clientWidth, h = container.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    startTimeRef.current = performance.now();

    const animate = (time) => {
      const elapsed = (time - startTimeRef.current) / 1000;
      const speed = (params.speed / 100) * 5;
      gl.uniform1f(u.time, elapsed * speed + params.offset * 0.01);
      gl.uniform2f(u.res, canvas.width, canvas.height);
      gl.uniform1f(u.px, window.devicePixelRatio || 1);
      gl.uniform1f(u.scale, params.scale);
      gl.uniform1f(u.rot, (params.rotation * Math.PI) / 180);
      const c1 = hexToRgba(params.color1), c2 = hexToRgba(params.color2), c3 = hexToRgba(params.color3);
      gl.uniform4f(u.c1, ...c1); gl.uniform4f(u.c2, ...c2); gl.uniform4f(u.c3, ...c3);
      gl.uniform1f(u.prop, params.proportion / 100);
      gl.uniform1f(u.soft, params.softness / 100);
      gl.uniform1f(u.shape, PatternShapes[params.shape]);
      gl.uniform1f(u.shapeScale, params.shapeSize / 100);
      gl.uniform1f(u.dist, params.distortion / 50);
      gl.uniform1f(u.swirl, params.swirl / 100);
      gl.uniform1f(u.swirlIt, params.swirl === 0 ? 0 : params.swirlIterations);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      frameIdRef.current = requestAnimationFrame(animate);
    };
    frameIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameIdRef.current !== undefined) cancelAnimationFrame(frameIdRef.current);
      ro.disconnect();
      gl.deleteProgram(program); gl.deleteShader(vert); gl.deleteShader(frag); gl.deleteBuffer(buf);
    };
  }, [isMounted, params]);

  return (
    <div ref={containerRef} className={className} style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden", ...style }}>
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}
